/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'

import {HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import {
    Box,
    Divider,
    Image,
    SimpleGrid,
    createStylesContext,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

import {useFooterData} from '../../clients/contentful/hooks'
import {styleConfigChakraKeys} from '../../constants'
import LinksList from '../links-list'
import FooterMeta from './footer-meta'
import NewsletterBar from './newsletter-bar'

const [StylesProvider] = createStylesContext('Footer')
const Footer = ({...otherProps}) => {
    const styles = useMultiStyleConfig(styleConfigChakraKeys.footer)
    const footerData = useFooterData()
    const footerCollectionItem = footerData.data?.footerCollection?.items[0]

    if (!footerCollectionItem) {
        return <div>Loading...</div>
    }

    return (
        <Box as="footer" {...styles.container} {...otherProps}>
            <Box p={{xl: '31px 46px'}} maxW="1920px" mx="auto">
                <StylesProvider value={styles}>
                    <NewsletterBar
                        cta={footerCollectionItem.cta}
                        languageSwitcher={footerCollectionItem.languageSwitcher}
                        newsLetterEmail={footerCollectionItem.newsletterEmail}
                        listCollection={
                            footerCollectionItem.listsCollection.items
                        }
                    />

                    <HideOnMobile>
                        <Divider {...styles.divider} />
                    </HideOnMobile>

                    <SimpleGrid columns={{base: 1, lg: 2}} spacing={3}>
                        <HideOnMobile>
                            <SimpleGrid columns={4} spacingX={10}>
                                {footerCollectionItem &&
                                    footerCollectionItem?.listsCollection.items.map(
                                        (item, index) => (
                                            <LinksList
                                                key={index}
                                                heading={item?.heading}
                                                links={
                                                    item?.itemsCollection?.items
                                                }
                                            />
                                        )
                                    )}
                            </SimpleGrid>
                        </HideOnMobile>

                        <Box {...styles.footerImageContainer}>
                            <Image
                                {...styles.image}
                                data-testid={'footer-banner'}
                                src={
                                    footerCollectionItem?.mediaAssetCollection
                                        .items[0].desktop.url
                                }
                                ignoreFallback={true}
                                alt={
                                    footerCollectionItem?.mediaAssetCollection
                                        .items[0].desktop.title
                                }
                            />
                        </Box>
                    </SimpleGrid>

                    <HideOnMobile>
                        <Divider {...styles.divider} />
                    </HideOnMobile>

                    <FooterMeta
                        copyright={footerCollectionItem?.copyright}
                        socialMediaIconsCollectionItems={
                            footerCollectionItem?.socialMediaIconsCollection
                                .items
                        }
                    />
                </StylesProvider>
            </Box>
        </Box>
    )
}

export default Footer
